import { useEffect, useState, useCallback, useMemo } from 'react'
import { useConnect, useAccount, useDisconnect } from 'wagmi'
import * as Sentry from '@sentry/react'

type MiniPayProviderState = {
  isMiniPay: boolean
  isAutoConnectEnabled: boolean
  isConnecting: boolean
  isConnected: boolean
  address: string | undefined
  error: string | null
  retry: () => void
}

const checkIsMiniPay = (): boolean => {
  if (typeof window === 'undefined') return false
  const ethereum = window.ethereum as { isMiniPay?: boolean } | undefined
  return Boolean(ethereum?.isMiniPay)
}

const checkAutoConnectEnabled = (): boolean => {
  if (typeof window === 'undefined') return false
  const urlParams = new URLSearchParams(window.location.search)
  // Check URL first, then sessionStorage (persists after email auth redirect)
  const autoConnectParam =
    urlParams.get('autoConnect') || sessionStorage.getItem('autoConnect')
  return autoConnectParam === 'true'
}

export const useMiniPayProvider = (): MiniPayProviderState => {
  const {
    connect,
    connectors,
    isPending: isConnecting,
    error: connectError,
  } = useConnect()
  const { address, isConnected } = useAccount()
  const { disconnect } = useDisconnect()

  const isMiniPay = useMemo(checkIsMiniPay, [])
  const isAutoConnectEnabled = useMemo(checkAutoConnectEnabled, [])
  const [error, setError] = useState<string | null>(null)
  const [hasAttemptedConnect, setHasAttemptedConnect] = useState(false)

  const attemptConnect = useCallback(() => {
    if (!isAutoConnectEnabled) return

    if (!isMiniPay) {
      const errorMsg =
        'MiniPay wallet not detected. Please open this page in the MiniPay app.'
      setError(errorMsg)

      Sentry.captureMessage(
        'MiniPay auto-connect failed: wallet not detected',
        {
          level: 'warning',
          tags: {
            error_type: 'minipay.not_detected',
            auto_connect: 'true',
          },
          extra: {
            hasEthereum:
              typeof window !== 'undefined' && Boolean(window.ethereum),
            userAgent:
              typeof navigator !== 'undefined'
                ? navigator.userAgent
                : 'unknown',
          },
        }
      )
      return
    }

    setError(null)
    setHasAttemptedConnect(true)

    const injectedConnector = connectors.find(
      (connector) =>
        connector.id === 'injected' || connector.type === 'injected'
    )

    if (!injectedConnector) {
      const errorMsg = 'Unable to find injected wallet connector'
      setError(errorMsg)

      Sentry.captureMessage(
        'MiniPay auto-connect failed: no injected connector',
        {
          level: 'error',
          tags: {
            error_type: 'minipay.no_connector',
          },
          extra: {
            availableConnectors: connectors.map((c) => ({
              id: c.id,
              type: c.type,
            })),
          },
        }
      )
      return
    }

    try {
      disconnect()
      connect({ connector: injectedConnector })
    } catch (err) {
      const errorMsg = 'Failed to connect to MiniPay wallet'
      setError(errorMsg)

      Sentry.captureException(err, {
        tags: {
          error_type: 'minipay.connect_error',
        },
      })
    }
  }, [isAutoConnectEnabled, isMiniPay, connectors, connect, disconnect])

  useEffect(() => {
    if (isAutoConnectEnabled && !hasAttemptedConnect && !isConnected) {
      attemptConnect()
    }
  }, [isAutoConnectEnabled, hasAttemptedConnect, isConnected, attemptConnect])

  useEffect(() => {
    if (connectError) {
      let errorMsg = 'Failed to connect to wallet'

      if (connectError.message.includes('rejected')) {
        errorMsg =
          'Connection was rejected. Please approve the connection in your wallet.'
      } else if (connectError.message.includes('already pending')) {
        errorMsg =
          'A connection request is already pending. Please check your wallet.'
      }

      setError(errorMsg)

      Sentry.captureException(connectError, {
        tags: {
          error_type: 'minipay.wagmi_connect_error',
        },
        extra: {
          isMiniPay,
          errorMessage: connectError.message,
        },
      })
    }
  }, [connectError, isMiniPay])

  useEffect(() => {
    if (isConnected && address && isAutoConnectEnabled) {
      setError(null)

      Sentry.addBreadcrumb({
        category: 'minipay',
        message: 'Successfully connected to MiniPay',
        level: 'info',
        data: {
          address: address.slice(0, 10) + '...',
        },
      })
    }
  }, [isConnected, address, isAutoConnectEnabled])

  const retry = useCallback(() => {
    setHasAttemptedConnect(false)
    setError(null)
    attemptConnect()
  }, [attemptConnect])

  return {
    isMiniPay,
    isAutoConnectEnabled,
    isConnecting,
    isConnected,
    address,
    error,
    retry,
  }
}
